import { ArrowBack, Notifications, Search, NavigateNext, NavigateBefore, ErrorOutline, CheckCircle, Refresh, Delete } from "@material-ui/icons";
import React, { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import "react-tabs/style/react-tabs.css";

import fetchClient from "../../api/fetchClient";
import Loader from "../../components/Loader";
import message from "../../helper/message";
import {
	Container,
	ModernHeader,
	HeaderLeft,
	HeaderContent,
	PageTitle,
	PageSubtitle,
	HeaderActions,
	BackButton,
	ResultsCard,
	SectionInfoTitle,
	SectionSubtitle,
	TableWrapper,
	StreamsTable,
	TableHead,
	TableRow,
	TableCell,
	TableActions,
	StatusBadge,
	EmptyState,
	EmptyTitle,
	EmptyText,
	getTheme,
} from "../../components/common";

function NotificacoesComerzzia() {
	const history = useHistory();
	const theme = getTheme('comerzzia');
	const [notificacoes, setNotificacoes] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [deletingId, setDeletingId] = useState(null);
	const [selectedIds, setSelectedIds] = useState(new Set());
	const [isBulkDeleting, setIsBulkDeleting] = useState(false);

	// Filter state
	const [searchTerm, setSearchTerm] = useState('');

	// Pagination state
	const [recordsPerPage, setRecordsPerPage] = useState(10);
	const [currentPage, setCurrentPage] = useState(1);

	// Filtered data
	const filteredNotificacoes = useMemo(() => {
		if (!searchTerm) return notificacoes;
		const term = searchTerm.toLowerCase();
		return notificacoes.filter(item => {
			const messageMatch = item.notification?.message && item.notification.message.toLowerCase().includes(term);
			const statusMatch  = item.notification?.status && item.notification.status.toLowerCase().includes(term);
			const typeMatch    = item.notification?.type && item.notification.type.toLowerCase().includes(term);
			const docMatch     = item.notification?.docNumber && item.notification.docNumber.toLowerCase().includes(term);
			const emailMatch   = item.targetEmails && item.targetEmails.toLowerCase().includes(term);
			const guidMatch    = item.guid && item.guid.toLowerCase().includes(term);
			return messageMatch || statusMatch || typeMatch || docMatch || emailMatch || guidMatch;
		});
	}, [notificacoes, searchTerm]);

	// Paginated data
	const paginatedNotificacoes = useMemo(() => {
		const sorted = filteredNotificacoes.slice().sort((a, b) => {
			return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
		});
		const startIndex = (currentPage - 1) * recordsPerPage;
		return sorted.slice(startIndex, startIndex + recordsPerPage);
	}, [filteredNotificacoes, currentPage, recordsPerPage]);

	const totalPages = Math.ceil(filteredNotificacoes.length / recordsPerPage);

	// Selection helpers (scoped to current page)
	const pageIds = paginatedNotificacoes.map(n => n.guid);
	const allPageSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.has(id));
	const somePageSelected = pageIds.some(id => selectedIds.has(id));

	// Reset page + selection when filter changes
	useEffect(() => {
		setCurrentPage(1);
		setSelectedIds(new Set());
	}, [searchTerm, recordsPerPage]);

	useEffect(() => { getAllNotificacoes(); }, []);

	const getAllNotificacoes = async (isRefresh = false) => {
		try {
			if (isRefresh) setIsRefreshing(true);
			else setIsLoading(true);
			const { data } = await fetchClient.get("/comerzzia/notificacoes");
			setNotificacoes(data);
			if (isRefresh) message.sucess("Notificações atualizadas!");
		} catch (error) {
			message.error(error.response?.data?.message || "Erro interno, contate o suporte!");
		} finally {
			if (isRefresh) setIsRefreshing(false);
			else setIsLoading(false);
		}
	};

	const toggleSelect = (id) => {
		setSelectedIds(prev => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const toggleSelectAll = () => {
		if (allPageSelected) {
			setSelectedIds(prev => {
				const next = new Set(prev);
				pageIds.forEach(id => next.delete(id));
				return next;
			});
		} else {
			setSelectedIds(prev => {
				const next = new Set(prev);
				pageIds.forEach(id => next.add(id));
				return next;
			});
		}
	};

	const onDelete = async (id) => {
		if (!window.confirm("Deseja realmente excluir esta notificação?")) return;
		try {
			setDeletingId(id);
			await fetchClient.delete(`/comerzzia/notificacoes/${id}`);
			message.sucess("Notificação excluída com sucesso");
			setNotificacoes(prev => prev.filter(n => n.guid !== id));
			setSelectedIds(prev => { const next = new Set(prev); next.delete(id); return next; });
		} catch (error) {
			message.error(error.response?.data?.message || "Erro ao excluir notificação");
		} finally {
			setDeletingId(null);
		}
	};

	const onBulkDelete = async () => {
		const count = selectedIds.size;
		if (!window.confirm(`Deseja realmente excluir ${count} notificaç${count > 1 ? 'ões' : 'ão'}?`)) return;
		try {
			setIsBulkDeleting(true);
			await Promise.all([...selectedIds].map(id => fetchClient.delete(`/comerzzia/notificacoes/${id}`)));
			message.sucess(`${count} notificaç${count > 1 ? 'ões excluídas' : 'ão excluída'} com sucesso`);
			setNotificacoes(prev => prev.filter(n => !selectedIds.has(n.guid)));
			setSelectedIds(new Set());
		} catch (error) {
			message.error(error.response?.data?.message || "Erro ao excluir notificações");
		} finally {
			setIsBulkDeleting(false);
		}
	};

	const formatDate = (dateString) => {
		if (!dateString) return "-";
		return new Date(dateString).toLocaleString('pt-BR', {
			day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
		});
	};

	const getStatusInfo = (status) => {
		switch (status) {
			case 'ERROR':   return { label: 'Erro',     active: false };
			case 'SUCCESS': return { label: 'Sucesso',  active: true  };
			case 'PENDING': return { label: 'Pendente', active: null  };
			default:        return { label: status || '-', active: null };
		}
	};

	if (isLoading) return <Loader />;

	return (
		<Container>
			<ModernHeader>
				<HeaderLeft>
					<BackButton color={theme.primary} onClick={() => history.goBack()}>
						<ArrowBack />
					</BackButton>
					<HeaderContent>
						<PageTitle>
							<Notifications style={{ marginRight: '12px', fontSize: '32px' }} />
							Notificações Comerzzia
						</PageTitle>
						<PageSubtitle>Visualize as notificações e alertas do sistema</PageSubtitle>
					</HeaderContent>
				</HeaderLeft>
				<HeaderActions>
					<RefreshButton onClick={() => getAllNotificacoes(true)} disabled={isRefreshing}>
						<Refresh style={{ marginRight: '8px', fontSize: '20px' }} />
						{isRefreshing ? 'Atualizando...' : 'Atualizar'}
					</RefreshButton>
				</HeaderActions>
			</ModernHeader>

			{notificacoes.length === 0 ? (
				<ResultsCard>
					<EmptyState>
						<Notifications style={{ fontSize: '48px', color: '#cbd5e1', marginBottom: '16px' }} />
						<EmptyTitle>Nenhuma notificação encontrada</EmptyTitle>
						<EmptyText>Não há notificações disponíveis no momento.</EmptyText>
					</EmptyState>
				</ResultsCard>
			) : (
				<ResultsCard>
					<SectionInfoWrapper>
						<SectionInfoLeft>
							<SectionInfoTitle>Notificações registradas</SectionInfoTitle>
							<SectionSubtitle>
								Mostrando {paginatedNotificacoes.length} de {filteredNotificacoes.length} {filteredNotificacoes.length === 1 ? 'notificação' : 'notificações'}
								{searchTerm && ` (filtrado de ${notificacoes.length})`}
							</SectionSubtitle>
						</SectionInfoLeft>
						<SearchContainer>
							<SearchIconWrapper>
								<Search style={{ fontSize: '20px' }} />
							</SearchIconWrapper>
							<SearchInput
								type="text"
								placeholder="Pesquisar por mensagem, status, tipo..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
							/>
							{searchTerm && (
								<ClearSearchButton onClick={() => setSearchTerm('')}>×</ClearSearchButton>
							)}
						</SearchContainer>
					</SectionInfoWrapper>

					{selectedIds.size > 0 && (
						<BulkActionBar>
							<BulkActionInfo>
								<BulkCheckMark>✓</BulkCheckMark>
								<span>
									<strong>{selectedIds.size}</strong>{' '}
									notificaç{selectedIds.size > 1 ? 'ões selecionadas' : 'ão selecionada'}
								</span>
							</BulkActionInfo>
							<BulkActionButtons>
								<BulkClearBtn onClick={() => setSelectedIds(new Set())}>
									Limpar seleção
								</BulkClearBtn>
								<BulkDeleteBtn onClick={onBulkDelete} disabled={isBulkDeleting}>
									{isBulkDeleting
										? <DeleteSpinner />
										: <Delete style={{ fontSize: 15, marginRight: 6 }} />
									}
									{isBulkDeleting ? 'Excluindo...' : `Excluir ${selectedIds.size}`}
								</BulkDeleteBtn>
							</BulkActionButtons>
						</BulkActionBar>
					)}

					<TableWrapper>
						<StreamsTable>
							<thead>
								<tr>
									<TableHead width="4%">
										<CheckboxInput
											type="checkbox"
											checked={allPageSelected}
											ref={el => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
											onChange={toggleSelectAll}
											title="Selecionar todos da página"
										/>
									</TableHead>
									<TableHead width="10%">Data/Hora</TableHead>
									<TableHead width="12%">GUID</TableHead>
									<TableHead width="8%">Tipo</TableHead>
									<TableHead width="21%">Mensagem</TableHead>
									<TableHead width="9%">Doc. Nº</TableHead>
									<TableHead width="9%">Status</TableHead>
									<TableHead width="18%">Último Erro</TableHead>
									<TableHead width="5%" align="right">Ações</TableHead>
								</tr>
							</thead>
							<tbody>
								{paginatedNotificacoes.length === 0 ? (
									<TableRow>
										<TableCell colSpan={9} style={{ textAlign: 'center', color: '#6b7280' }}>
											Nenhum resultado encontrado para os filtros aplicados
										</TableCell>
									</TableRow>
								) : (
									paginatedNotificacoes.map((notif) => {
										const statusInfo = getStatusInfo(notif.notification?.status);
										const isSelected = selectedIds.has(notif.guid);
										return (
											<TableRow key={notif.guid} style={isSelected ? { background: '#f0f9ff' } : {}}>
												<TableCell>
													<CheckboxInput
														type="checkbox"
														checked={isSelected}
														onChange={() => toggleSelect(notif.guid)}
													/>
												</TableCell>
												<TableCell>
													<DateText>{formatDate(notif.createdAt)}</DateText>
												</TableCell>
												<TableCell>
													<GuidText title={notif.guid}>{notif.guid || "-"}</GuidText>
												</TableCell>
												<TableCell>
													<TypeBadge>{notif.notification?.type || "-"}</TypeBadge>
												</TableCell>
												<TableCell>
													<MessageText title={notif.notification?.message}>
														{notif.notification?.message || "Sem mensagem"}
													</MessageText>
												</TableCell>
												<TableCell>
													{notif.notification?.docNumber || "-"}
												</TableCell>
												<TableCell>
													<StatusBadge active={statusInfo.active}>
														{statusInfo.active === true  && <CheckCircle style={{ fontSize: '14px', marginRight: '4px' }} />}
														{statusInfo.active === false && <ErrorOutline style={{ fontSize: '14px', marginRight: '4px' }} />}
														{statusInfo.label}
													</StatusBadge>
												</TableCell>
												<TableCell>
													<ErrorText title={notif.lastError}>{notif.lastError || "-"}</ErrorText>
												</TableCell>
												<TableActions>
													<DeleteBtn
														onClick={() => onDelete(notif.guid)}
														disabled={deletingId === notif.guid}
													>
														{deletingId === notif.guid
															? <DeleteSpinner />
															: <Delete style={{ fontSize: 16 }} />
														}
													</DeleteBtn>
												</TableActions>
											</TableRow>
										);
									})
								)}
							</tbody>
						</StreamsTable>
					</TableWrapper>

					<PaginationWrapper>
						<RecordsPerPageContainer>
							<RecordsPerPageLabel>Registros por página:</RecordsPerPageLabel>
							<RecordsPerPageSelect
								value={recordsPerPage}
								onChange={(e) => setRecordsPerPage(Number(e.target.value))}
							>
								<option value={5}>5</option>
								<option value={10}>10</option>
								<option value={25}>25</option>
								<option value={50}>50</option>
								<option value={100}>100</option>
							</RecordsPerPageSelect>
						</RecordsPerPageContainer>

						<PaginationContainer>
							<PaginationButton
								disabled={currentPage === 1}
								onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
							>
								<NavigateBefore />
							</PaginationButton>
							<PageIndicator>Página {currentPage} de {totalPages || 1}</PageIndicator>
							<PaginationButton
								disabled={currentPage >= totalPages}
								onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
							>
								<NavigateNext />
							</PaginationButton>
						</PaginationContainer>
					</PaginationWrapper>
				</ResultsCard>
			)}
		</Container>
	);
}

export default NotificacoesComerzzia;

// ===== STYLED COMPONENTS =====

const spin = keyframes`to { transform: rotate(360deg); }`;
const fadeIn = keyframes`from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); }`;

const SectionInfoWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    padding: 24px 16px 0 16px;
    gap: 16px;
    @media (max-width: 768px) { flex-direction: column; }
`;

const SectionInfoLeft = styled.div`
    display: flex;
    flex-direction: column;
`;

const SearchContainer = styled.div`
    display: flex;
    align-items: center;
    position: relative;
    min-width: 300px;
    @media (max-width: 768px) { min-width: 100%; width: 100%; }
`;

const SearchIconWrapper = styled.div`
    position: absolute;
    left: 12px;
    color: #9ca3af;
    display: flex;
    align-items: center;
    pointer-events: none;
`;

const SearchInput = styled.input`
    width: 100%;
    padding: 10px 40px 10px 44px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    font-family: 'Mulish', sans-serif;
    transition: all 0.2s ease;
    &:focus { outline: none; border-color: #00bcd4; box-shadow: 0 0 0 3px rgba(0, 188, 212, 0.1); }
    &::placeholder { color: #9ca3af; }
`;

const ClearSearchButton = styled.button`
    position: absolute;
    right: 8px;
    width: 24px;
    height: 24px;
    border: none;
    border-radius: 50%;
    background: #e5e7eb;
    color: #6b7280;
    font-size: 16px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    &:hover { background: #d1d5db; color: #374151; }
`;

// ─── Bulk action bar ──────────────────────────────────────────
const BulkActionBar = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    margin: 0 0 12px 0;
    background: linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 100%);
    border: 1px solid #80deea;
    border-radius: 10px;
    animation: ${fadeIn} 0.2s ease;
`;

const BulkActionInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    font-weight: 600;
    color: #006064;
    font-family: 'Mulish', sans-serif;
`;

const BulkCheckMark = styled.span`
    width: 24px;
    height: 24px;
    background: #00bcd4;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    flex-shrink: 0;
`;

const BulkActionButtons = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
`;

const BulkClearBtn = styled.button`
    padding: 7px 16px;
    border: 1.5px solid #80deea;
    border-radius: 8px;
    background: white;
    color: #00838f;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: 'Mulish', sans-serif;
    transition: all 0.2s ease;
    &:hover { background: #e0f7fa; }
`;

const BulkDeleteBtn = styled.button`
    display: flex;
    align-items: center;
    padding: 7px 16px;
    border: none;
    border-radius: 8px;
    background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
    color: white;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    font-family: 'Mulish', sans-serif;
    transition: all 0.2s ease;
    box-shadow: 0 2px 6px rgba(231, 76, 60, 0.3);
    &:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 4px 10px rgba(231, 76, 60, 0.4); }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const CheckboxInput = styled.input`
    width: 16px;
    height: 16px;
    cursor: pointer;
    accent-color: #00bcd4;
`;

// ─── Row actions ──────────────────────────────────────────────
const DeleteBtn = styled.button`
    width: 32px;
    height: 32px;
    border: 1.5px solid #fecaca;
    border-radius: 8px;
    background: white;
    color: #e74c3c;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
    &:hover:not(:disabled) { background: #fef2f2; border-color: #e74c3c; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const DeleteSpinner = styled.div`
    width: 12px;
    height: 12px;
    border: 2px solid #fecaca;
    border-top-color: #e74c3c;
    border-radius: 50%;
    animation: ${spin} 0.7s linear infinite;
`;

// ─── Pagination ───────────────────────────────────────────────
const PaginationWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
`;

const RecordsPerPageContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const RecordsPerPageLabel = styled.span`
    font-size: 14px;
    color: #6b7280;
    font-family: 'Mulish', sans-serif;
`;

const RecordsPerPageSelect = styled.select`
    padding: 8px 12px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    font-family: 'Mulish', sans-serif;
    background: white;
    cursor: pointer;
    transition: all 0.2s ease;
    &:focus { outline: none; border-color: #00bcd4; }
`;

const PaginationContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 12px;
`;

const PaginationButton = styled.button`
    width: 40px;
    height: 40px;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10px;
    background: linear-gradient(135deg, #00bcd4 0%, #4dd0e1 100%);
    color: #fff;
    font-size: 24px;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 2px 8px rgba(0, 188, 212, 0.3);
    &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 188, 212, 0.4); }
    &:active:not(:disabled) { transform: translateY(0); }
    &:disabled { background: #bdbdbd; cursor: not-allowed; box-shadow: none; }
`;

const PageIndicator = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: #2c3e50;
    font-family: 'Mulish', sans-serif;
    padding: 0 16px;
`;

// ─── Table content ────────────────────────────────────────────
const DateText = styled.span`
    font-size: 13px;
    color: #374151;
    font-family: 'Mulish', sans-serif;
`;

const TypeBadge = styled.span`
    display: inline-block;
    padding: 4px 12px;
    background: #e0f2fe;
    color: #0369a1;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    font-family: 'Mulish', sans-serif;
`;

const MessageText = styled.span`
    display: block;
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    color: #1f2937;
    font-family: 'Mulish', sans-serif;
`;

const ErrorText = styled.span`
    display: block;
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    color: #dc2626;
    font-family: 'Mulish', sans-serif;
`;

const GuidText = styled.span`
    display: block;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 12px;
    color: #6b7280;
    font-family: 'Mulish', monospace;
    background: #f3f4f6;
    padding: 4px 8px;
    border-radius: 4px;
`;

const RefreshButton = styled.button`
    background: linear-gradient(135deg, #00bcd4 0%, #4dd0e1 100%);
    color: white;
    border: none;
    border-radius: 12px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    font-family: 'Mulish', sans-serif;
    box-shadow: 0 2px 8px rgba(0, 188, 212, 0.3);
    &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 188, 212, 0.4); }
    &:active:not(:disabled) { transform: translateY(0); }
    &:disabled { background: #bdbdbd; cursor: not-allowed; box-shadow: none; opacity: 0.6; }
`;
